import * as React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { ErrorLogComponent } from '@cian/error-log-component';
import { Responsive, UiKitRoot } from '@cian/ui-kit';
import { uaToDeviceType } from '@cian/ui-kit/utils';

import { PageLayout } from '../../components/PageLayout';
import { useLocationChange } from '../../hooks/browserHistory';
import { selectIsCompilation } from '../../selectors/pagination';
import { IApplicationContext } from '../../types/applicationContext';
import { IHelmetContext } from '../../types/helmet';
import { TReduxStore } from '../../types/redux';
import { TThunkDispatch } from '../../types/redux/thunk';
import { ApplicationContext } from '../../utils/applicationContext';
import { BreadcrumbsContainer } from '../BreadcrumbsContainer';
import { ErrorContainer } from '../ErrorContainer';
import { HeaderContainer } from '../Header';
import { ListingEntryContainer } from '../ListingEntry';
import { PageDescriptionContainer } from '../PageDescriptionContainer';
import { PageLoaderContainer } from '../PageLoaderContainer';
import { PageMetaContainer } from '../PageMeta';
import { PromoDetailsModalContainer } from '../PromoDetailsModalContainer';
import { SearchResultsContainer } from '../SearchResults';
import { SeoMarkedListContainer } from '../SeoMarkedListContainer';

export interface IApplicationProps {
  context: IApplicationContext;
  reduxStore: TReduxStore;
  helmetContext?: IHelmetContext;
}

export const Application: React.FC<IApplicationProps> = ({ reduxStore, context, helmetContext }) => {
  const {
    logger,
    custom: { userAgent },
  } = context;

  const isCompilation = selectIsCompilation(reduxStore.getState());

  const dispatch = reduxStore.dispatch as TThunkDispatch;

  useLocationChange(dispatch);

  return (
    <ErrorLogComponent logger={logger}>
      <ApplicationContext.Provider value={context}>
        <UiKitRoot deviceType={uaToDeviceType(userAgent || undefined)}>
          <HelmetProvider context={helmetContext}>
            <Provider store={reduxStore}>
              <PageLayout>
                <PageMetaContainer />
                <SeoMarkedListContainer />
                {!isCompilation && (
                  <Responsive phone>
                    <PageDescriptionContainer isCompilation={false} />
                  </Responsive>
                )}
                <BreadcrumbsContainer />
                <HeaderContainer />
                <SearchResultsContainer />
                <PromoDetailsModalContainer />
                <ErrorContainer />
                <PageLoaderContainer />
                <ListingEntryContainer />
              </PageLayout>
            </Provider>
          </HelmetProvider>
        </UiKitRoot>
      </ApplicationContext.Provider>
    </ErrorLogComponent>
  );
};
